// Phrases that pick between keys by what they are given: the choice is code,
// every word is still a key. A phrase a key and its plural can say alone is a
// t() call at its use instead, not a function here.
import { t } from "./core";

type Step = `exams:examSetSteps.${"write" | "kit" | "check" | "assign" | "watch" | "review"}` | `exams:examSitSteps.${"start" | "solve" | "handIn" | "grade"}`;
/** A step of setting or sitting an exam: its title and what it does are words, its commands code. */
const step = (key: Step, commands?: string[]) => ({ title: t(`${key}.title` as const), text: t(`${key}.text` as const), commands });

export const phrases = {
  /** How long a live share has run, at a glance: 12m, 3h, 2d. */
  elapsed: (ms: number) => {
    const m = Math.max(0, Math.floor(ms / 60_000));
    if (m < 1) return t("shares:elapsedNow");
    if (m < 60) return t("shares:elapsedMinutes", { n: m });
    if (m < 48 * 60) return t("shares:elapsedHours", { n: Math.floor(m / 60) });
    return t("shares:elapsedDays", { n: Math.floor(m / 1440) });
  },
  expiresSoon: (days: number) => (days <= 1 ? t("shares:expiresWithinDay") : t("shares:expiresInDays", { n: days })),
  /** A source kind as a person reads it; RFC-043's names too, from an older relay. */
  source: (s: string) => {
    const k = `shares:source.${s}`;
    return t(k as "shares:source.byj", { defaultValue: s });
  },
  agentCapLine: (spent: number, cap: number, set: boolean) =>
    t(set ? "shares:agentCapLine" : "shares:agentCapLineDefault", { spent: `$${spent.toFixed(2)}`, cap: `$${cap.toFixed(2)}` }),

  foldedTurn: (answered: boolean, cost?: string) =>
    !answered ? t("session:foldedInProgress") : cost ? t("session:foldedAnsweredCost", { cost }) : t("session:foldedAnswered"),
  shareClosed: (reason: string) => (reason === "ended" ? t("session:shareEnded") : t("session:shareEndedWhy", { reason })),
  inviteMade: (role: string, uses: number, expires: string) =>
    uses === 0 ? t("session:inviteMadeAnyone", { role, expires }) : t("session:inviteMade", { count: uses, role, expires }),
  /** How far a link has got: "1/5", or "3 joined" for anyone with it. */
  inviteLinkUses: (maxUses: number, used: number) => (maxUses === 0 ? t("session:inviteLinkJoined", { used }) : `${used}/${maxUses}`),
  /** What each role may do in this share, by what its session can carry. */
  inviteRoleWhat: (role: string, caps: { terminal?: boolean; inject?: boolean; agent?: boolean }) =>
    role === "caller"
      ? t("session:inviteRoleWhat.caller")
      : role === "coach"
        ? caps.terminal
          ? t("session:inviteRoleWhat.coachTerminal")
          : caps.inject
            ? t("session:inviteRoleWhat.coachInject")
            : t("session:inviteRoleWhat.coach")
        : caps.terminal
          ? t("session:inviteRoleWhat.spectatorTerminal")
          : t("session:inviteRoleWhat.spectator"),
  inviteUsesOption: (n: number) => (n === 0 ? t("session:inviteUsesAnyone") : t("session:inviteUses", { count: n })),
  /** How long an invite link lasts: its seconds, and as a person reads it. */
  ttlOptions: (): [number, string][] => [
    [3600, t("session:ttl.hour")],
    [86400, t("session:ttl.day")],
    [604800, t("session:ttl.week")],
  ],

  /** A terminal share's recording has no turns: it is the screen (RFC-043 model 21). */
  recordingTurns: (from: number, to: number) =>
    from === 0 && to === 0
      ? t("recordings:recordingTerminal")
      : from === to
        ? t("recordings:recordingTurn", { n: from })
        : t("recordings:recordingTurnRange", { from, to }),
  /** Who a comment is mailed to, under the form that writes it (byj-1wv.52). */
  mailedTo: (names: string[]) => {
    const who =
      names.length === 1
        ? names[0]!
        : names.length <= 3
          ? t("recordings:mailedAnd", { list: names.slice(0, -1).join(", "), last: names[names.length - 1] })
          : t("recordings:mailedOthers", { list: names.slice(0, 2).join(", "), n: names.length - 2 });
    return t("recordings:mailedTo", { who });
  },
  openThreadCount: (n: number) => (n === 0 ? t("recordings:openThreadsNone") : t("recordings:openThreadCount", { count: n })),
  reviewersDone: (done: number, all: number) => (all === 0 ? t("recordings:reviewersNone") : t("recordings:reviewersDone", { done, count: all })),
  daysLeft: (n: number) => (n <= 0 ? t("recordings:daysLeftNow") : n === 1 ? t("recordings:daysLeftLast") : t("recordings:daysLeft", { n })),
  retentionChoice: (days: number) => (days === 365 ? t("recordings:retentionYear") : t("recordings:retentionDays", { count: days })),
  /** A change made to several picked at once, said back (byj-1wv.51.13). */
  bulkDone: (done: number, refused: string[]) => {
    const changed = t("recordings:bulkChanged", { count: done });
    if (!refused.length) return changed;
    const first = refused[0]!;
    const why = refused.length > 1 ? t("recordings:bulkRefusedMore", { n: refused.length, first }) : t("recordings:bulkRefused", { n: refused.length, first });
    return `${changed} ${why}`;
  },
  deleteRecordingsConfirm: (n: number, forInstructor = false) =>
    forInstructor ? t("recordings:deleteRecordingsConfirmClass", { count: n }) : t("recordings:deleteRecordingsConfirm", { count: n }),
  /** `cls`: the class whose instructor can open it, who loses it too. */
  deleteRecordingConfirm: (title: string, cls?: string) =>
    cls ? t("recordings:deleteRecordingConfirmClass", { title, cls }) : t("recordings:deleteRecordingConfirm", { title }),
  /** A task score (RFC-037) and its band. */
  scoreChip: (total: number, band?: string) => `${total}${band ? ` · ${band}` : ""}`,

  machineHomeKept: (days: number) => (days === 0 ? t("machines:machineHomeKeptNext") : t("machines:machineHomeKeptFor", { count: days })),
  machineHours: (seconds: number) => {
    const m = Math.round(seconds / 60);
    if (m === 0 && seconds > 0) return t("machines:hoursUnderMinute");
    if (m < 60) return t("machines:hoursMinutes", { m });
    const h = Math.floor(m / 60);
    return m % 60 ? t("machines:hoursHoursMinutes", { h, m: m % 60 }) : t("machines:hoursHours", { h });
  },

  templateVia: (via: string) =>
    via === "invite" ? t("templates:templateViaInvite") : via.startsWith("class:") ? t("templates:templateViaClass", { name: via.slice("class:".length) }) : via,
  templateBytes: (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`),
  templateNotUploadedNote: (vs: number[]) => t("templates:templateNotUploadedNote", { count: vs.length, versions: vs.map((n) => `v${n}`).join(", ") }),

  fileAdded: (r: { email: string; registered: boolean; sent: boolean; error?: string }) =>
    r.error
      ? `${r.email}: ${r.error}`
      : r.registered
        ? t(r.sent ? "files:fileAddedSeesMailed" : "files:fileAddedSees", { email: r.email })
        : t("files:fileAddedInvited", { email: r.email }),
  filePlace: (line: number, path?: string) => (path ? `${path}:${line}` : t("files:filePlaceLine", { line })),
  /** Who sees a file of mine besides me: people and classes, or only me. */
  fileSeenBy: (viewers: number, classes: number) =>
    viewers === 0 && classes === 0
      ? t("files:fileSeenOnlyYou")
      : [viewers > 0 ? t("files:fileSeenPeople", { count: viewers }) : "", classes > 0 ? t("files:fileSeenClasses", { count: classes }) : ""]
          .filter(Boolean)
          .join(" · "),
  fileVersionOption: (n: number, latest: boolean) => (latest ? t("files:fileVersionLatest", { n }) : `v${n}`),

  deleteExamConfirm: (title: string, classes: number) =>
    t("exams:deleteExamConfirm", {
      title,
      what: classes === 0 ? t("exams:deleteExamNoClass") : classes === 1 ? t("exams:deleteExamOneClass") : t("exams:deleteExamClasses", { n: classes }),
    }),
  submitWhat: (xs: string[]) => t("exams:submitWhat", { list: xs.join(", ") }),
  barCost: (n: number) => t("exams:barCost", { cost: n.toFixed(2) }),
  gradeAgentsUsed: (xs: string[]) => t("exams:gradeAgentsUsed", { list: xs.join(", ") }),
  gradeProcessNone: (why: string) => why || t("exams:gradeNotScored"),
  /** An exam I set, at a glance: its versions and the classes it is assigned to. */
  examTeachingRow: (versions: number, assignments: number) =>
    `${t("exams:examVersions", { count: versions })} · ${assignments === 0 ? t("exams:examRowNotAssigned") : t("exams:examRowAssigned", { count: assignments })}`,
  /** Where an exam stands in setting it, in order (byj-1wv.51.12). */
  stageSteps: () => [t("exams:stageSteps.write"), t("exams:stageSteps.check"), t("exams:stageSteps.assign"), t("exams:stageSteps.grade")],
  /** The steps that set an exam, each with the commands it runs; `code` the class's join code. */
  examSetSteps: (code: string) => [
    step("exams:examSetSteps.write"),
    step("exams:examSetSteps.kit", ["byj ask 'agent$exam-kit: pull my exams from Share and finish the new one'", "byj exam pull KIT --watch --publish"]),
    step("exams:examSetSteps.check", ["byj exam validate KIT", "byj exam calibrate KIT --agent claude"]),
    step("exams:examSetSteps.assign", [`byj exam publish KIT --class ${code}`]),
    step("exams:examSetSteps.watch", ["byj exam grade KIT --relay"]),
    step("exams:examSetSteps.review", ["byj exam regrade KIT --relay SUBMISSION"]),
  ],
  /** The steps that sit an exam's question, each with the commands it runs. */
  examSitSteps: (assignment: string, kind: string) => [
    step("exams:examSitSteps.start", [`byj exam start ${assignment} QUESTION --agent ${kind}`]),
    step("exams:examSitSteps.solve"),
    step("exams:examSitSteps.handIn", ["byj exam submit", "byj exam status"]),
    step("exams:examSitSteps.grade"),
  ],

  /**
   * How a machine is used, step by step (byj-1wv.51.15), as the machine there
   * is: none yet, Start first; an exam's, its home its own, so its agents are
   * signed in to again. Signing in is one step, each agent a choice in it.
   */
  machineInsideSteps: (running: boolean, exam: boolean) => [
    {
      title: t("machines:insideSteps.open.title"),
      text: running ? t("machines:insideSteps.open.text") : t("machines:insideSteps.open.textNone"),
      note: t("machines:insideSteps.open.note"),
    },
    {
      title: t("machines:insideSteps.signIn.title"),
      text: t("machines:insideSteps.signIn.text"),
      choices: [
        { title: t("machines:signInChoices.claude.title"), text: t("machines:signInChoices.claude.text"), note: t("machines:signInChoices.claude.note"), commands: ["claude"] },
        { title: t("machines:signInChoices.codex.title"), text: t("machines:signInChoices.codex.text"), note: t("machines:signInChoices.codex.note"), commands: ["codex login --device-auth"] },
      ],
    },
    {
      title: t("machines:insideSteps.work.title"),
      text: t("machines:insideSteps.work.text"),
      note: exam ? t("machines:insideSteps.work.noteExam") : t("machines:insideSteps.work.note"),
    },
  ],
  /** How a machine is run on one's own computer, step by step, its docker run once it is known. */
  machineOwnSteps: (command?: string) => [
    { title: t("machines:ownSteps.docker.title"), text: t("machines:ownSteps.docker.text"), note: t("machines:ownSteps.docker.note") },
    { title: t("machines:ownSteps.run.title"), text: t("machines:ownSteps.run.text"), note: t("machines:ownSteps.run.note"), commands: command ? [command] : undefined },
    { title: t("machines:ownSteps.approve.title"), text: t("machines:ownSteps.approve.text"), note: t("machines:ownSteps.approve.note") },
    { title: t("machines:ownSteps.open.title"), text: t("machines:ownSteps.open.text"), note: t("machines:ownSteps.open.note") },
  ],

  pickingStatus: (n: number) => (n === 0 ? t("classes:pickingNone") : t("classes:pickingSome", { n })),
  assignmentSetN: (done: number, of: number) =>
    done === of ? t("classes:assignmentSetAll", { count: of }) : t("classes:assignmentSetSome", { done, of }),
  classRecordingsSummary: (n: number, students: number) =>
    t("classes:classRecordingsSummary", { recordings: t("classes:recordingsN", { count: n }), students: t("classes:studentsN", { count: students }) }),
};
